import { IconDownload } from "@tabler/icons-react";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Group, RegionCargando, Skeleton, Text, ThemeIcon } from "@/components/ui";
import { extensionDe, nodoIconoPorExtension } from "../utils/documento-helpers";
import { DocumentoPreviewContent, type DocumentoPreview } from "./DocumentoPreviewContent";

/**
 * Modal envolvente para la previsualización · header con icono + nombre,
 * body con el viewer (delegado a `DocumentoPreviewContent`), footer con
 * acciones (descargar / cerrar).
 *
 * Si `preview` es null (todavía cargando), muestra una hoja de documento
 * skeleton (el tipo real aún no se conoce); si está
 * presente, delega al content. Esto evita que el modal se vea vacío
 * mientras el blob fetch está en vuelo.
 */
export function DocumentoPreviewModal({
  opened,
  preview,
  nombre,
  descargando,
  onCerrar,
  onDescargar,
}: {
  opened: boolean;
  preview: DocumentoPreview | null;
  nombre: string;
  descargando: boolean;
  onCerrar: () => void;
  onDescargar: () => void;
}) {
  const ext = extensionDe(nombre);
  return (
    <Dialog open={opened} onOpenChange={(next) => !next && onCerrar()}>
      <DialogContent className="flex max-w-4xl flex-col p-0">
        <DialogHeader className="mb-0 px-5 pt-5">
          <DialogTitle>
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="filled" color="multiva" size="md">
                {nodoIconoPorExtension(ext, 14, 1.7)}
              </ThemeIcon>
              <Text fw={600} truncate span>
                {nombre}
              </Text>
            </Group>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 border-t border-border bg-background" style={{ height: "70vh" }}>
          {preview ? (
            <DocumentoPreviewContent preview={preview} nombre={nombre} />
          ) : (
            <HojaSkeleton />
          )}
        </div>
        <Group justify="flex-end" gap="xs" className="p-3">
          <Button variant="default" leftSection={<IconDownload size={14} />} loading={descargando} onClick={onDescargar}>
            Descargar
          </Button>
          <Button variant="subtle" onClick={onCerrar}>
            Cerrar
          </Button>
        </Group>
      </DialogContent>
    </Dialog>
  );
}

/** Hoja de documento en carga · página centrada con encabezado y párrafos. */
function HojaSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando vista previa…" className="flex h-full justify-center overflow-hidden p-6">
      <div className="flex h-full w-full max-w-2xl flex-col gap-3 rounded-md border border-border bg-card p-8 shadow-sm" aria-hidden="true">
        <div className="flex items-start justify-between gap-4">
          <Skeleton height={18} width="40%" />
          <Skeleton height={40} width={64} />
        </div>
        <Skeleton height={10} width="30%" />
        <div className="mt-4 flex flex-col gap-2.5">
          {["95%", "90%", "97%", "70%"].map((w, i) => (
            <Skeleton key={i} height={10} width={w} />
          ))}
        </div>
        <Skeleton height={90} className="mt-4" />
        <div className="mt-4 flex flex-col gap-2.5">
          {["92%", "85%", "60%"].map((w, i) => (
            <Skeleton key={i} height={10} width={w} />
          ))}
        </div>
      </div>
    </RegionCargando>
  );
}
